import { computed, ref, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import { useApolloClient } from '@vue/apollo-composable'
import { APOLLO_CLIENTS } from 'common/dictionary.js'
import { useManufacturersStore } from 'stores/manufacturers'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useSsrStore } from 'src/stores/ssr'
import { useSystemStore } from 'src/stores/system'
import { useUserAuthStore } from 'src/stores/user/auth'
import { BASE_URL } from 'src/common/config'
import { capitalizeFirstLetter, clearTrailingSlash, consoleLogAPI, getLocale } from 'src/common/functions'
import { getConfig } from 'src/configs'
import { getConsumerLocale } from 'src/core/locale/runtime'
import competitionldJson from 'src/seo/competition'
import gameldJson from 'src/seo/game.js'
import gameGroupldJson from 'src/seo/gameGroup.js'
import gamesByManufacturerldJson from 'src/seo/gamesByManufacturer'
import { buildHeadInput, buildSeoMeta } from 'src/seo/meta'
import getCompetition from 'src/apollo/queries/getCompetition.gql'
import getGroup from 'src/apollo/queries/getGroup.gql'
import getManufacturerSeo from 'src/apollo/queries/getManufacturerSeo.gql'
import getMarkdownContent from 'src/apollo/queries/getMarkdownContent.gql'
import getPageSeoData from 'src/apollo/queries/getPageSeoData.gql'
import getSeoData from 'src/apollo/queries/getSeoData.gql'
import getSportsbookSeo from 'src/apollo/queries/getSportsbookSeo.gql'

const config = getConfig()
const alternateLinkRegexp = /<link\b(?=[^>]*\srel\s*=\s*["']alternate["'])[^>]*>/gi
const canonicalLinkRegexp = /<link\b(?=[^>]*\srel\s*=\s*["']canonical["'])[^>]*\shref\s*=\s*["']([^"']+)["'][^>]*>/i

function getSeoPageUrl(href, pathSuffix = '') {
  try {
    const url = new URL(href)

    if (pathSuffix && pathSuffix !== '/') {
      url.pathname = `${clearTrailingSlash(url.pathname)}${clearTrailingSlash(pathSuffix)}`
    }

    url.search = ''
    url.hash = ''

    return url
  } catch {
    return null
  }
}

function getSeoLinks(rawProps, pathSuffix = '') {
  if (!rawProps) {
    return { alternateLinks: [], canonicalLink: null }
  }

  let html = typeof rawProps === 'string' ? rawProps : rawProps.html || ''

  try {
    html = typeof rawProps === 'string' ? JSON.parse(rawProps)?.html || rawProps : html
  } catch {
    html = rawProps
  }

  let alternateIndex = 0
  const alternateLinks = Array.from(html.matchAll(alternateLinkRegexp), ([tag]) => {
    const href = tag.match(/\shref\s*=\s*(["'])(.*?)\1/i)?.[2]?.trim()
    const url = getSeoPageUrl(href, pathSuffix)

    if (!url) {
      return null
    }

    const hreflang = tag.match(/\shreflang\s*=\s*(["'])(.*?)\1/i)?.[2]?.trim()
    let key = `alternate-${hreflang}`

    if (!hreflang) {
      alternateIndex += 1
      key = alternateIndex === 1 ? 'alternate' : `alternate-${alternateIndex}`
    }

    return {
      key,
      rel: 'alternate',
      href: url.href,
      ...(hreflang ? { hreflang } : {}),
    }
  }).filter(Boolean)

  return {
    alternateLinks,
    canonicalLink: getSeoPageUrl(html.match(canonicalLinkRegexp)?.[1]?.trim(), pathSuffix)?.href || null,
  }
}

function getPathWithoutLocale(fullPath, locale) {
  if (!locale) {
    return fullPath
  }

  const localePrefix = `/${locale}`

  if (fullPath === localePrefix) {
    return '/'
  }

  if (fullPath.startsWith(`${localePrefix}/`) || fullPath.startsWith(`${localePrefix}?`)) {
    return fullPath.slice(localePrefix.length)
  }

  return fullPath
}

function identityTitleTemplate(text) {
  return text ? `${text}` : ''
}

function buildLdJsonScript(innerHTML) {
  if (!innerHTML) {
    return null
  }

  return {
    type: 'application/ld+json',
    innerHTML,
  }
}

function getOrigin() {
  return typeof location === 'undefined' ? '' : location.origin
}

function getPageKeyFromRoute(route) {
  const pathWithoutLocale = getPathWithoutLocale(route.path, route.params.locale)

  return pathWithoutLocale.split('/').filter(Boolean).join('/')
}

export function useMetadata() {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const { resolveClient } = useApolloClient()
  const apolloClient = resolveClient()
  const apolloClientGo = resolveClient(APOLLO_CLIENTS.clientGo)
  const manufacturersStore = useManufacturersStore()
  const siteInfoStore = useSiteInfoStore()
  const ssrStore = useSsrStore()
  const systemStore = useSystemStore()
  const userAuthStore = useUserAuthStore()
  const getGameObjectData = computed(() => ssrStore.gameObjectData)
  const getGameObject = computed(() => ssrStore.gameObject)

  const baseHead = computed(() => {
    const currentDomain = siteInfoStore.getCurrentDomain
    const currentLocale = getConsumerLocale({
      consumer: 'seo',
      getFallbackLocale: () => route.params.locale || userAuthStore.getUserLanguage || getLocale(),
    })
    const pathWithoutLocale = getPathWithoutLocale(route.path, route.params.locale || currentLocale)
    const { alternateLinks, canonicalLink: customCanonicalLink } = getSeoLinks(
      siteInfoStore.siteInfo?.propsContent?.props,
      pathWithoutLocale,
    )
    const canonicalLink =
      customCanonicalLink ||
      (currentDomain ? getSeoPageUrl(`https://${currentDomain}${BASE_URL}/`, route.path)?.href : null)
    let robots = alternateLinks.some(({ hreflang }) => hreflang) ? null : 'noindex'

    if (route.query.manufacturer) {
      robots = 'noindex, follow'
    }

    return buildHeadInput(
      {},
      {
        alternateLinks,
        canonicalLink,
        links: [
          {
            key: 'favicon_mask',
            rel: 'mask-icon',
            href: 'img/safari-pinned-tab.svg',
            color: config.themeColor,
          },
        ],
        metaTags: [{ property: 'og:type', content: 'website' }],
        robots,
        titleTemplate: identityTitleTemplate,
      },
    )
  })

  function makeHead(resolved = {}) {
    const seoData = resolved.seoData ?? null
    const seoMeta = buildSeoMeta(seoData, resolved.fallback)

    siteInfoStore.updateSeoData(seoData)

    return buildHeadInput(seoMeta, {
      links: resolved.links,
      metaTags: resolved.metaTags,
      scripts: resolved.scripts,
      titleTemplate: resolved.titleTemplate,
    })
  }

  async function resolvePageSeoData(page, fallback = {}) {
    const { data: result } = await apolloClient.query({
      query: getPageSeoData,
      variables: {
        page,
      },
    })

    return {
      fallback,
      seoData: result?.pageSeoData ?? null,
    }
  }

  function resolveRoutePageSeo(fallback = {}) {
    return resolvePageSeoData(getPageKeyFromRoute(route), fallback)
  }

  async function resolveHomeSeo() {
    const { data: result } = await apolloClient.query({ query: getSeoData })

    return {
      fallback: {
        title: t('navLinks.home'),
      },
      seoData: result?.siteInfo?.seoData ?? null,
    }
  }

  async function resolveCompetitionSeo() {
    const { data: result } = await apolloClientGo.query({
      query: getCompetition,
      variables: {
        id: Number.parseInt(route.params.id, 10),
        isGuest: !userAuthStore.getAuth,
      },
    })
    const competition = result?.newCompetition ?? result?.competition
    const fallbackTitle = competition?.name ? `${t('tournament')} - ${competition.name}` : t('tournament')

    return {
      fallback: {
        title: fallbackTitle,
        description: competition?.shortDescription,
      },
      scripts:
        systemStore.isBot && competition
          ? [
              buildLdJsonScript(
                new competitionldJson({
                  $route: route,
                  competition,
                }).generate(),
              ),
            ]
          : [],
      seoData: competition?.seoData ?? null,
    }
  }

  function resolveGamePageSeo() {
    const game = getGameObjectData.value
    const gameObject = getGameObject.value
    const manufacturer = manufacturersStore.getManufacturerById(game?.manufacturerId)
    const providerName = manufacturer?.name ?? ''
    const fallbackTitle = game?.name
      ? t('gameTitleTemplate', {
          gameName: game.name,
          company: config.shortTitle,
        })
      : ''
    const fallbackDescription = game?.name
      ? t('gameDescTemplate', {
          gameName: game.name,
          providerName,
        })
      : ''
    const metaTags = []
    const scripts = []

    if (game?.gameId) {
      const imageLink = game.compressedImage ? game.compressedImage.webp?.md || game.compressedImage.webp?.lg : null

      if (imageLink) {
        const domain = process.env.CDN_DOMAIN_BACK ? process.env.CDN_DOMAIN_BACK : `${getOrigin()}/`
        const image = `${domain}${imageLink}`

        metaTags.push({
          property: 'og:image',
          content: image,
        })
      }

      if (systemStore.isBot && manufacturer) {
        scripts.push(
          buildLdJsonScript(
            new gameldJson({
              metaDesc: buildSeoMeta(game?.seoData, { description: fallbackDescription }).description,
              getGameObjectData: game,
              getGameObject: gameObject,
              manufacturer,
            }).generate(),
          ),
        )
      }
    }

    return {
      fallback: {
        title: fallbackTitle,
        description: fallbackDescription,
      },
      metaTags,
      scripts,
      seoData: game?.seoData ?? null,
    }
  }

  async function resolveGameGroupSeo(groupSlug = route.params.group || 'slot') {
    const { data: result } = await apolloClientGo.query({
      query: getGroup,
      variables: { slug: groupSlug },
    })
    const group = result?.gameGroup
    const subgroupSlug = route.params.subgroup
    const subgroup = group && subgroupSlug ? group.subgroups.find((item) => item.slug === subgroupSlug) : null
    const seoData = subgroup?.seoData ?? group?.seoData ?? null
    const fallbackTitle = subgroup?.name ?? group?.name ?? (groupSlug ? capitalizeFirstLetter(groupSlug) : '')

    return {
      fallback: {
        title: fallbackTitle,
      },
      group,
      scripts:
        systemStore.isBot && group
          ? [
              buildLdJsonScript(
                new gameGroupldJson({
                  $route: route,
                  games: { gameGroup: group },
                  group,
                  subgroup,
                }).generate(),
              ),
            ]
          : [],
      seoData,
      subgroup,
    }
  }

  async function resolveCasinoSeo() {
    const resolved = await resolveGameGroupSeo('slot')
    const metaTags = [...(resolved.metaTags ?? [])]

    if (resolved.group?.name === 'Casino' && systemStore.isBot) {
      metaTags.push(
        { name: 'prerender-status-code', content: '301' },
        {
          name: 'prerender-header',
          content: `Location: ${getOrigin()}${router.resolve({ name: 'GamesByGroup', params: { group: 'slot' } }).path}`,
        },
      )
    }

    return {
      ...resolved,
      metaTags,
    }
  }

  async function resolveGamesByManufacturerSeo() {
    const { data: result } = await apolloClient.query({
      query: getManufacturerSeo,
      variables: { manufacturer: route.params.manufacturer },
    })
    const manufacturer = result?.manufacturer
    const fallbackTitle =
      manufacturer?.name ?? (route.params.manufacturer ? capitalizeFirstLetter(route.params.manufacturer) : '')

    return {
      fallback: {
        title: fallbackTitle,
        description: fallbackTitle,
      },
      scripts:
        systemStore.isBot && manufacturer
          ? [
              buildLdJsonScript(
                new gamesByManufacturerldJson({
                  $route: route,
                  manufacturer,
                }).generate(),
              ),
            ]
          : [],
      seoData: manufacturer?.seoData ?? null,
    }
  }

  async function resolveSportsSeo() {
    const { data: result } = await apolloClient.query({ query: getSportsbookSeo })

    return {
      fallback: {
        title: t('navLinks.sport'),
      },
      seoData: result?.sportsbookSeo ?? null,
    }
  }

  async function resolveMarkdownRuleSeo() {
    const { data: result } = await apolloClient.query({
      query: getMarkdownContent,
      variables: { slug: route.params.rule },
    })

    return {
      fallback: {
        title: result?.markdownContent?.title,
      },
    }
  }

  const routeSeoResolvers = {
    Home: resolveHomeSeo,
    apps: () => resolvePageSeoData('apps', { title: t('downloadApp') }),
    Competition: resolveCompetitionSeo,
    GamePageNew: resolveGamePageSeo,
    cashback: () => resolvePageSeoData('cashback', { title: t('titles.cashback') }),
    Casino: resolveCasinoSeo,
    'drops-wins': () => resolvePageSeoData('drops-wins', { title: t('titles.dropsWins') }),
    '404page': () => ({
      fallback: {
        title: t('titles.err404'),
      },
      metaTags: [{ name: 'prerender-status-code', content: '404' }],
    }),
    rules: () => resolvePageSeoData('rules', { title: t('titles.rules') }),
    ruleCrypto: () => resolvePageSeoData('rules/crypto', { title: t('cryptocurrency') }),
    Favorites: () => ({
      fallback: {
        title: t('favorites') || t('navLinks.home'),
      },
    }),
    BonusGames: () => ({
      fallback: {
        title: t('promoGames'),
      },
    }),
    GamesByGroup: resolveGameGroupSeo,
    GamesBySubGroup: resolveGameGroupSeo,
    GamesByManufacturer: resolveGamesByManufacturerSeo,
    Poker: () => resolvePageSeoData('poker', { title: t('poker') }),
    Sports: resolveSportsSeo,
    rule: resolveMarkdownRuleSeo,
    Competitions: () =>
      resolveRoutePageSeo({
        title: t('titles.tournaments'),
        description: t('descriptions.tournaments'),
      }),
    Promotions: () =>
      resolveRoutePageSeo({
        title: t('Promotions'),
      }),
    PromotionsAll: () =>
      resolvePageSeoData('promo/all', {
        title: t('Promotions'),
      }),
    euro_slots: () =>
      resolvePageSeoData('euro_slots', {
        title: t('euro_slot.title', { platformSite: config.platformSite }),
        description: t('euro_slot.description', { platform: config.title }),
      }),
  }

  async function routeSeo(name) {
    const resolver = routeSeoResolvers[name]

    if (resolver) {
      return makeHead(await resolver())
    }

    if (route.meta?.seoPage) {
      const page = typeof route.meta.seoPage === 'function' ? route.meta.seoPage(route) : route.meta.seoPage

      return makeHead(await resolvePageSeoData(page, route.meta.seoFallback))
    }

    return makeHead()
  }

  const extraHead = ref({})
  let routeSeoRequestId = 0

  watchEffect(() => {
    const requestId = ++routeSeoRequestId
    const name = route.name

    routeSeo(name)
      .then((head) => {
        if (requestId === routeSeoRequestId) {
          extraHead.value = head
        }
      })
      .catch((error) => {
        if (requestId === routeSeoRequestId) {
          siteInfoStore.updateSeoData(null)
          extraHead.value = {}
        }

        consoleLogAPI(error)
      })
  })

  useHead(() => baseHead.value)

  useHead(() => extraHead.value)
}
